// The quote drawer's edit state (#1559): the fields, the line items, Save and
// Discard. Seeded once per quote id (the drawer is keyed by it) and re-seeded
// from a refetch only while nothing is unsaved.
import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useUpdateQuote, type QuoteRow } from "@/hooks/useQuotes";
import type { QuoteTemplateRow } from "@/hooks/useQuoteTemplates";
import { calcQuoteTotals, type QuoteTotals } from "@/lib/quote-totals";
import { templateToFormPrefill } from "@/lib/quoteTemplates";
import { isPickup } from "@/types/quotes";
import { useLineItemDrafts } from "../useLineItemDrafts";
import { canEditQuote, opensEditing } from "./drawerLogic";
import {
  applyUnitMode,
  buildSavePayload,
  draftEquals,
  fieldsFromQuote,
  type DrawerDefaults,
  type DrawerFields,
  type UnitMode,
} from "./drawerPayload";

export type DraftField = Exclude<keyof DrawerFields, "lines">;
type DraftValues = Omit<DrawerFields, "lines">;
type Lines = ReturnType<typeof useLineItemDrafts>;
/** Save details passes the two quote fields it edits (Approach §5). */
export type SaveOverrides = Partial<Pick<DrawerFields, "serviceType" | "eventTypeId">>;

export const SAVE_REFUSED_ACCEPTED =
  "This quote was accepted, so your changes weren't saved. Discard them to see the accepted version.";
export const SAVE_REFUSED_SQUARE =
  "This quote is managed in Square, so your changes weren't saved. Discard them and change it in Square.";
export const SAVE_CHECK_FAILED = "Couldn't check this quote before saving. Try again.";

const num = (x: unknown) => Number(x) || 0;
const valuesOf = ({ lines: _lines, ...rest }: DrawerFields): DraftValues => rest;

/** Stored totals for a saved quote, so Square's tax is never recomputed. */
function storedTotals(q: QuoteRow): QuoteTotals {
  return {
    subtotal: num(q.subtotal),
    discount_amount: num(q.discount_amount),
    delivery_amount: num(q.delivery_amount),
    service_charge_amount: num(q.service_charge_amount),
    gratuity_amount: num(q.gratuity_amount),
    tax_amount: num(q.tax_amount),
    total: num(q.total),
  };
}

function liveTotals(f: DrawerFields): QuoteTotals {
  const gratuity = f.gratuityOn ? num(f.gratuityPercent) : 0;
  const delivery = isPickup(f.serviceType) ? 0 : f.delivery;
  // Save writes tax_rate 13, so the live figure uses it too.
  return calcQuoteTotals(f.lines, 13, f.discount, delivery, f.serviceCharge, gratuity);
}

/**
 * The fresh read before a Save writes. The `quote` prop can be stale: nothing
 * refetches when the client accepts on the public page. Null means go ahead;
 * otherwise the toast to show. Fails closed on an error or a missing row.
 */
async function saveRefusal(id: string): Promise<string | null> {
  const { data, error } = await supabase.from("quotes").select("status, square_source").eq("id", id).single();
  if (error || !data) return SAVE_CHECK_FAILED;
  if (canEditQuote({ status: data.status, square_source: data.square_source ?? false })) return null;
  return data.square_source ? SAVE_REFUSED_SQUARE : SAVE_REFUSED_ACCEPTED;
}

/** Fields, snapshot and edit mode, re-seeded from the quote only while clean. */
function useSeededDraft(quote: QuoteRow, defaults: DrawerDefaults) {
  const [seed] = useState(() => fieldsFromQuote(quote, defaults));
  const [values, setValues] = useState<DraftValues>(() => valuesOf(seed));
  const [snapshot, setSnapshot] = useState<DrawerFields>(seed);
  const [editing, setEditing] = useState(() => opensEditing(quote));
  const lines = useLineItemDrafts(() => seed.lines);
  const { replaceLines, resizePerPerson } = lines;
  const fields: DrawerFields = { ...values, lines: lines.lineItems };
  const dirty = !draftEquals(fields, snapshot);

  // What the seed was taken from. A refetch while dirty is skipped, so
  // discard() can tell the quote moved and re-seed then.
  const [seededFrom, setSeededFrom] = useState({ at: quote.updated_at, status: quote.status, deposit: defaults.defaultDepositPct });
  const stale = seededFrom.at !== quote.updated_at || seededFrom.deposit !== defaults.defaultDepositPct;
  const reseed = useCallback(() => {
    const next = fieldsFromQuote(quote, defaults);
    setValues(valuesOf(next));
    replaceLines(next.lines);
    setSnapshot(next);
    // A status change decides edit mode again (Send leaves it, Back to draft enters it).
    if (seededFrom.status !== quote.status) setEditing(opensEditing(quote));
    setSeededFrom({ at: quote.updated_at, status: quote.status, deposit: defaults.defaultDepositPct });
  }, [quote, defaults, replaceLines, seededFrom.status]);
  useEffect(() => {
    if (stale && !dirty) reseed();
  }, [stale, dirty, reseed]);

  // While dirty, only per-person quantities follow the event's guest count.
  const guests = useRef(quote.guest_count);
  useEffect(() => {
    if (guests.current === quote.guest_count) return;
    guests.current = quote.guest_count;
    if (dirty) resizePerPerson(num(quote.guest_count));
  }, [quote.guest_count, dirty, resizePerPerson]);

  return { values, setValues, snapshot, setSnapshot, lines, fields, dirty, stale, reseed, editing, setEditing };
}

/** The field setters: plain fields, gratuity, the unit toggle and Load template. */
function useDraftEditors(quote: QuoteRow, lines: Lines, setValues: Dispatch<SetStateAction<DraftValues>>, defaultGratuityPct = 0) {
  const { lineItems, updateLine, replaceLines, appendLines } = lines;
  const guests = num(quote.guest_count);
  const set = useCallback(<K extends DraftField>(key: K, value: DrawerFields[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
  }, [setValues]);
  // Ticking gratuity on with no percent yet starts from the brand default (#982).
  const toggleGratuity = useCallback((on: boolean) => {
    setValues((v) => ({ ...v, gratuityOn: on, gratuityPercent: on && !(v.gratuityPercent > 0) ? defaultGratuityPct : v.gratuityPercent }));
  }, [setValues, defaultGratuityPct]);
  const setUnitMode = useCallback((i: number, mode: UnitMode) => {
    const line = lineItems[i];
    if (!line) return;
    const next = applyUnitMode(line, mode, guests);
    updateLine(i, "pricing_type", next.pricing_type);
    updateLine(i, "quantity", next.quantity);
  }, [lineItems, guests, updateLine]);
  // Notes and event type fill only when empty; service type and guests stay (#1547).
  const loadTemplate = useCallback((t: QuoteTemplateRow, mode: "replace" | "add") => {
    const prefill = templateToFormPrefill(t, guests, "merge");
    if (prefill.lineItems.length === 0) return;
    if (mode === "replace") replaceLines(prefill.lineItems);
    else appendLines(prefill.lineItems);
    setValues((v) => ({
      ...v,
      notes: v.notes.trim() ? v.notes : (prefill.notes ?? v.notes),
      eventTypeId: v.eventTypeId || (prefill.eventTypeId ?? ""),
    }));
  }, [guests, replaceLines, appendLines, setValues]);
  return { set, toggleGratuity, setUnitMode, loadTemplate };
}

/** Save: the fresh status read, then useUpdateQuote. Resolves to whether it saved. */
function useDraftSave(quote: QuoteRow, onSaved: (saved: DrawerFields, overrides: SaveOverrides) => void) {
  const qc = useQueryClient();
  const updateQuote = useUpdateQuote();
  const [checking, setChecking] = useState(false);
  // useUpdateQuote deletes and re-inserts the line items with no transaction, so two
  // saves at once can leave every item doubled. A second call refuses while one runs.
  const running = useRef(false);
  const save = async (fields: DrawerFields, overrides: SaveOverrides = {}): Promise<boolean> => {
    if (running.current) return false;
    running.current = true;
    const next: DrawerFields = { ...fields, ...overrides };
    setChecking(true);
    try {
      const refusal = await saveRefusal(quote.id);
      if (refusal) {
        toast.error(refusal);
        qc.invalidateQueries({ queryKey: ["quotes"] });
        return false;
      }
      await updateQuote.mutateAsync({ id: quote.id, ...buildSavePayload(quote, next) });
    } catch {
      // useUpdateQuote already toasted; the edits stay for another try.
      return false;
    } finally {
      running.current = false;
      setChecking(false);
    }
    onSaved(next, overrides);
    return true;
  };
  return { save, saving: checking || updateQuote.isPending };
}

export function useQuoteDrawerDraft(quote: QuoteRow, defaults: DrawerDefaults = {}) {
  const d = useSeededDraft(quote, defaults);
  const editors = useDraftEditors(quote, d.lines, d.setValues, defaults.defaultGratuityPct);
  const canEdit = canEditQuote(quote);
  const editing = d.editing && canEdit;
  const { save, saving } = useDraftSave(quote, (saved, overrides) => {
    d.setValues((v) => ({ ...v, ...overrides }));
    d.setSnapshot(saved);
    if (quote.status !== "draft") d.setEditing(false);
  });
  const discard = () => {
    // The quote moved while dirty (the re-seed was skipped): show the latest.
    if (d.stale) d.reseed();
    else {
      d.setValues(valuesOf(d.snapshot));
      d.lines.replaceLines(d.snapshot.lines);
    }
    if (quote.status !== "draft") d.setEditing(false);
  };
  return {
    editing,
    canEdit,
    dirty: d.dirty,
    fields: d.fields,
    set: editors.set,
    lines: { ...d.lines, setUnitMode: editors.setUnitMode },
    toggleGratuity: editors.toggleGratuity,
    loadTemplate: editors.loadTemplate,
    totals: editing ? liveTotals(d.fields) : storedTotals(quote),
    startEdit: () => {
      if (canEdit) d.setEditing(true);
    },
    discard,
    save: (overrides?: SaveOverrides) => save(d.fields, overrides),
    saving,
  };
}

export type QuoteDrawerDraft = ReturnType<typeof useQuoteDrawerDraft>;
